import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { usePermissions } from '../../lib/session';
import { LockedPage } from '../../shell/ModulePages';
import { PipelineTab } from './board/PipelineTab';
import { CommissionsTab } from './commissions/CommissionsTab';
import { CreateDealDialog } from './dialogs/CreateDealDialog';
import { isDealsTab, type DealsTab } from './DealsTabs';
import { applyFilterToParams, filterFromParams } from './filters';
import { InvoicingTab } from './invoicing/InvoicingTab';
import { DealsList } from './list/DealsList';
import { usePipelines } from './api';
import type { DealFilter, ListSort, ListStatus } from './types';

export interface DealsPageProps {
  /** `/deals/new?contactId=`: the create dialog (Deals 03/04) is open above the page. */
  createOpen?: boolean | undefined;
}

/**
 * Tools › Deals: the tab bar Pipeline · Liste · Abrechnung · Provisionen (`?tab=`), the Immobilie /
 * Finanzierung switch (`?pipeline=`), the filters in the URL and the create dialog on `/deals/new`.
 */
export function DealsPage({ createOpen = false }: DealsPageProps) {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [params, setParams] = useSearchParams();
  const pipelines = usePipelines(permissions.tool('deals') === 'ok');

  const tabParam = params.get('tab');
  const tab: DealsTab = isDealsTab(tabParam) ? tabParam : 'pipeline';
  const pipelineKind = params.get('pipeline') === 'financing' ? 'financing' : 'property';
  const filter = filterFromParams(params);
  const expanded = params.get('late') === 'expanded';
  const status: ListStatus = params.get('status') === 'lost' ? 'lost' : 'active';
  const sortParam = params.get('sort');
  const sort: ListSort = sortParam === 'value' || sortParam === 'contact' ? sortParam : 'stageAge';

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setFilter = useCallback(
    (next: DealFilter) => {
      setParams((current) => applyFilterToParams(current, next), { replace: true });
    },
    [setParams],
  );

  const onTabChange = useCallback(
    (next: DealsTab) => patch({ tab: next === 'pipeline' ? null : next }),
    [patch],
  );
  const onPipelineKindChange = useCallback(
    (kind: 'property' | 'financing') => patch({ pipeline: kind === 'financing' ? 'financing' : null }),
    [patch],
  );
  const openCreate = useCallback(() => {
    const contactId = params.get('contactId');
    void navigate(`/deals/new${contactId ? `?contactId=${encodeURIComponent(contactId)}` : ''}`);
  }, [navigate, params]);
  const closeCreate = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete('contactId');
    void navigate({ pathname: '/deals', search: next.toString() ? `?${next.toString()}` : '' });
  }, [navigate, params]);

  if (permissions.tool('deals') !== 'ok') return <LockedPage tool="deals" />;

  const pipelineId = pipelines.data?.property.id ?? null;

  return (
    <>
      {tab === 'pipeline' ? (
        <PipelineTab
          filter={filter}
          onFilterChange={setFilter}
          expanded={expanded}
          onExpandedChange={(value) => patch({ late: value ? 'expanded' : null })}
          pipelineKind={pipelineKind}
          onPipelineKindChange={onPipelineKindChange}
          onTabChange={onTabChange}
          onCreate={openCreate}
        />
      ) : null}
      {tab === 'list' ? (
        <DealsList
          filter={filter}
          onFilterChange={setFilter}
          status={status}
          onStatusChange={(value) => patch({ status: value === 'lost' ? 'lost' : null })}
          sort={sort}
          onSortChange={(value) => patch({ sort: value === 'stageAge' ? null : value })}
          pipelineKind={pipelineKind}
          onPipelineKindChange={onPipelineKindChange}
          onTabChange={onTabChange}
          onCreate={openCreate}
        />
      ) : null}
      {tab === 'invoicing' ? <InvoicingTab onTabChange={onTabChange} pipelineId={pipelineId} /> : null}
      {tab === 'commissions' ? <CommissionsTab onTabChange={onTabChange} /> : null}
      <CreateDealDialog
        open={createOpen}
        contactId={params.get('contactId')}
        onClose={closeCreate}
        onCreated={(deal) => void navigate(`/deals/${deal.id}`)}
        onOpenDeal={(dealId) => void navigate(`/deals/${dealId}`)}
      />
    </>
  );
}
